import React, { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Icon, Typography } from '@packages/ui/shared';
import { VIN } from '../types';
import { openVinSession, updateRectAttrs } from '../utils/vinSessionUtils';
import { useAuthState } from '@packages/contexts/auth/auth.hooks';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { useVinSessionDispatch, useVinSessionState } from '../context/vin-sessions.hooks';
import { vinSessionActions } from '../context/vin-sessions.state';
import SubaruLogo from '../assets/placeholder_car_image.svg?react';
import { handleCopy } from '../utils/vinSessionUtils';

type VinDetailsProps = {
    car: VIN;
    handleClick: (car: VIN) => void;
    style?: any;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    onHide: () => void;
};

/** Below this the row cannot hold six columns without wrapping words, so it stacks onto three lines. */
const STACK_AT = 760;

const useStyles = createUseStyles({
    vinDetailsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        borderRadius: 4,
    },
    imgContainer: {
        width: 56,
        minWidth: 56,
        height: 34,
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        maxWidth: 56,
        maxHeight: 34,
        objectFit: 'contain',
    },
    /**
     * The columns are flexible, so a long colour or trim is cut with an ellipsis instead of
     * wrapping mid-word and pushing the row taller.
     */
    vinDetails: {
        flexGrow: 1,
        minWidth: 0,
        display: 'grid',
        gridTemplateColumns: '168px 52px minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1fr)',
        gap: 16,
        alignItems: 'center',
        [`@media (max-width: ${STACK_AT}px)`]: {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: '2px 12px',
        },
    },
    cell: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    vinNumber: {
        minWidth: 0,
        padding: 0,
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: 15,
        fontWeight: 400,
        color: '#007BFF',
        textTransform: 'uppercase',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': {
            textDecoration: 'underline',
        },
        [`@media (max-width: ${STACK_AT}px)`]: {
            gridColumn: '1 / -1',
        },
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 4,
        flexShrink: 0,
    },
    iconButton: {
        width: 34,
        height: 34,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: 4,
        background: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f3f3f3',
        },
    },
    dataCardTextOuter: {
        color: '#071424',
        fontSize: 13,
        marginTop: 8,
        width: '100%',
        gap: 10,
    },
});

const VinDetails: FC<VinDetailsProps> = (props) => {
    const { car, handleClick, style, setIsLoading, onHide, isLoading } = props;
    const { vin, model, modelYear, colorDescription, trimDescription } = car.vinRecord;
    const svgRef = useRef<SVGSVGElement | null>(null);
    const classes = useStyles();
    const { account } = useAuthState();
    const itemClasses = `d-flex align-items-center ${classes.dataCardTextOuter}`;
    const { selectedVin } = useVinSessionState();
    const [imageError, setImageError] = useState<boolean>(false);
    const dispatch = useVinSessionDispatch();

    const openSessionClick = (vin: string) => {
        window.open(`/vin/${vin}`, '_blank', 'noopener,noreferrer');
        openVinSession(vin, account?.accountId, setIsLoading, selectedVin);
        onHide();
    };

    useEffect(() => {
        if (svgRef.current) {
            const rectElement = svgRef.current.querySelector('rect');
            if (rectElement) {
                updateRectAttrs(rectElement);
            }
        }
    }, [imageError, svgRef]);

    return (
        <div className={`${classes.vinDetailsContainer}`} style={style}>
            {isLoading ? (
                <div className={itemClasses}>
                    <SkeletonLoader type="text" className="w-full" />
                    <SkeletonLoader type="text" className="w-full" />
                    <SkeletonLoader type="text" className="w-full" />
                    <SkeletonLoader type="text" className="w-full" />
                </div>
            ) : (
                <>
                    <div className={classes.imgContainer}>
                        {!imageError ? (
                            <img
                                src={car.vehicleImageUrl}
                                className={classes.image}
                                loading="lazy"
                                alt=""
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <SubaruLogo ref={svgRef} data-testid="rect" />
                        )}
                    </div>
                    <div className={classes.vinDetails}>
                        <button
                            type="button"
                            onClick={() => handleClick(car)}
                            className={classes.vinNumber}
                            data-testId="vin-click"
                            title={vin}
                        >
                            {vin}
                        </button>
                        <span className={classes.cell}>
                            <Typography variant="vin">{modelYear}</Typography>
                        </span>
                        <span className={classes.cell} title={model}>
                            <Typography variant="vin">{model}</Typography>
                        </span>
                        <span className={classes.cell} title={colorDescription}>
                            <Typography variant="labelRegular">{colorDescription}</Typography>
                        </span>
                        <span className={classes.cell} title={trimDescription}>
                            <Typography variant="labelRegular">{trimDescription}</Typography>
                        </span>
                    </div>
                    <div className={classes.actions} data-testId="action-Items">
                        <button
                            type="button"
                            className={classes.iconButton}
                            aria-label={`Copy the VIN ${vin}`}
                            onClick={() =>
                                handleCopy(
                                    vin,
                                    dispatch,
                                    vinSessionActions.setToastMessage,
                                    'VIN number copied to clipboard',
                                    'white',
                                    'checkmark'
                                )
                            }
                        >
                            <Icon name="copy" color="grayFive" />
                        </button>
                        <button
                            type="button"
                            className={classes.iconButton}
                            aria-label={`Open a VIN session for ${vin} in a new tab`}
                            onClick={() => openSessionClick(vin)}
                        >
                            <Icon name="open-in-new" color="grayFive" />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default VinDetails;
